/**
 * Lightness adjustment percentage for color variants
 */
const LIGHTNESS_PERCENTAGE = 3;

/**
 * Threshold where sRGB transitions from linear to gamma-corrected
 * Used to linearize sRGB color values for accurate luminance calculations
 */
const SRGB_LINEARIZATION_THRESHOLD = 0.04045;

/**
 * Linear segment slope coefficient for sRGB values at or below threshold
 */
const SRGB_LINEAR_COEFFICIENT = 12.92;

/**
 * Gamma expansion constants for sRGB values above threshold
 */
const SRGB_GAMMA_OFFSET = 0.055;
const SRGB_GAMMA_DIVISOR = 1.055;
const SRGB_GAMMA_EXPONENT = 2.4;

/**
 * WCAG relative luminance coefficients
 * Weights for red, green, and blue channels based on human perception
 * Source: https://www.w3.org/TR/WCAG20-TECHS/G17.html
 */
const LUMINANCE_RED_COEFFICIENT = 0.2126;
const LUMINANCE_GREEN_COEFFICIENT = 0.7152;
const LUMINANCE_BLUE_COEFFICIENT = 0.0722;

const SOFT_BLACK = '#1a1a1a';
const PURE_WHITE = '#FFFFFF';
const PURE_BLACK = '#000000';

/**
 * WCAG relative luminance threshold for readable text
 * Colors with luminance above this value should use dark text
 */
const READABLE_LUMINANCE_THRESHOLD = 0.179;

/**
 * WCAG AA minimum contrast ratio for normal text
 */
export const WCAG_AA_MIN_CONTRAST = 4.5;

/**
 * Saturation threshold above which APCA is used instead of WCAG
 * Highly saturated colors (>70%) benefit from APCA's perceptual model
 */
export const SATURATION_THRESHOLD = 0.7;

// APCA W3 v0.0.98G Constants
// Source: https://github.com/Myndex/SAPC-APCA
// Formula: https://github.com/Myndex/SAPC-APCA/blob/master/documentation/APCA-W3-LaTeX.md
const APCA_SRGB_RED = 0.2126729;
const APCA_SRGB_GREEN = 0.7151522;
const APCA_SRGB_BLUE = 0.072175;
const APCA_MAIN_TRC = 2.4;
const APCA_BG_NORM = 0.56;
const APCA_TEXT_NORM = 0.57;
const APCA_REVERSE_TEXT = 0.62;
const APCA_REVERSE_BG = 0.65;
const APCA_BLACK_THRESHOLD = 0.022;
const APCA_BLACK_CLAMP = 1.414;
const APCA_SCALE = 1.14;
const APCA_LOW_OFFSET = 0.027;
const APCA_LOW_CLIP = 0.1;

/**
 * HSL color representation
 */
export interface HSL {
  h: number; // Hue: 0-360
  s: number; // Saturation: 0-100
  l: number; // Lightness: 0-100
}

/**
 * Convert hex color to HSL color space
 *
 * @param hex Hex color string (e.g., '#FF7A59')
 * @returns HSL color representation
 */
export function hexToHsl(hex: string): HSL {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / diff + 2) / 6;
        break;
      case b:
        h = ((r - g) / diff + 4) / 6;
        break;
      default:
        break;
    }
  }

  return {
    h: h * 360,
    s: s * 100,
    l: l * 100,
  };
}

/**
 * Convert HSL color to hex color string
 *
 * @param hsl HSL color representation
 * @returns Hex color string (e.g., '#FF7A59')
 */
export function hslToHex(hsl: HSL): string {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  let r;
  let g;
  let b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (n: number) => {
    const hex = Math.round(n * 255).toString(16);
    return hex.length === 1 ? `${'0'}${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Clamp a value between minimum and maximum bounds
 *
 * @param value Value to clamp
 * @param min Minimum bound
 * @param max Maximum bound
 * @returns Clamped value
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Adjust the lightness of an HSL color
 *
 * @param hsl HSL color representation
 * @param amount Amount to adjust lightness by (can be positive or negative)
 * @returns New HSL color with adjusted lightness
 */
function adjustLightness(hsl: HSL, amount: number): HSL {
  return {
    ...hsl,
    l: clamp(hsl.l + amount, 0, 100),
  };
}

/**
 * Adjust the saturation of an HSL color
 *
 * @param hsl HSL color representation
 * @param amount Amount to adjust saturation by (can be positive or negative)
 * @returns New HSL color with adjusted saturation
 */
function adjustSaturation(hsl: HSL, amount: number): HSL {
  return {
    ...hsl,
    s: clamp(hsl.s + amount, 0, 100),
  };
}

/**
 * Create a neutral gray with specified lightness
 *
 * @param hsl Original HSL color (unused, kept for consistency)
 * @param targetLightness Target lightness value (0-100)
 * @returns Neutral gray HSL color
 */
function toNeutralGray(hsl: HSL, targetLightness: number): HSL {
  return {
    h: 0,
    s: 0,
    l: targetLightness,
  };
}

/**
 * Color variants for interactive states
 */
export interface ColorVariants {
  default: string;
  hover: string;
  pressed: string;
  pressedSubtle: string;
  disabled: string;
  subtle: string;
}

/**
 * Derive color variants for interactive states from a base color
 *
 * Generates hover, pressed, disabled, and subtle variants using HSL manipulation:
 * - hover: Slightly lighter (or darker if base is very light)
 * - pressed: Slightly darker (or lighter if base is very dark)
 * - disabled: Neutral gray with high lightness
 * - subtle: Very light tint with reduced saturation
 *
 * @param baseColor Hex color string (e.g., '#FF7A59')
 * @returns Object with all five color variants
 */
export function deriveColorVariants(baseColor: string): ColorVariants {
  const baseHsl = hexToHsl(baseColor);

  const isVeryLight = baseHsl.l > 95;
  const lightnessAdjust = isVeryLight
    ? LIGHTNESS_PERCENTAGE * 1.5
    : LIGHTNESS_PERCENTAGE;

  const hoverHsl =
    baseHsl.l > 85
      ? adjustLightness(baseHsl, -lightnessAdjust)
      : adjustLightness(baseHsl, LIGHTNESS_PERCENTAGE);

  let pressedSubtleHsl: HSL;
  if (isVeryLight) {
    pressedSubtleHsl = {
      h: baseHsl.h,
      s: Math.min(baseHsl.s, 3),
      l: Math.max(baseHsl.l - 4, 93),
    };
  } else {
    pressedSubtleHsl = adjustSaturation(
      { ...baseHsl },
      -Math.min(baseHsl.s * 0.2, 40)
    );
    pressedSubtleHsl = adjustLightness(
      pressedSubtleHsl,
      Math.max(93 - pressedSubtleHsl.l, 0)
    );
  }

  const pressedHsl =
    baseHsl.l < 15
      ? adjustLightness(baseHsl, LIGHTNESS_PERCENTAGE)
      : adjustLightness(baseHsl, -(lightnessAdjust * 1.5));

  const disabledHsl = toNeutralGray(baseHsl, 92);

  let subtleHsl: HSL;

  if (isVeryLight) {
    // Darken very light colors to ensure visible contrast with base
    subtleHsl = {
      h: baseHsl.h,
      s: Math.min(baseHsl.s, 5),
      l: Math.max(baseHsl.l - 3, 90),
    };
  } else {
    subtleHsl = adjustSaturation(
      { ...baseHsl },
      -Math.min(baseHsl.s * 0.7, 50)
    );
    subtleHsl = adjustLightness(subtleHsl, Math.max(96 - subtleHsl.l, 0));
  }

  return {
    default: baseColor,
    hover: hslToHex(hoverHsl),
    pressed: hslToHex(pressedHsl),
    pressedSubtle: hslToHex(pressedSubtleHsl),
    disabled: hslToHex(disabledHsl),
    subtle: hslToHex(subtleHsl),
  };
}

/**
 * RGB color representation
 */
interface RGB {
  r: number; // Red: 0-255
  g: number; // Green: 0-255
  b: number; // Blue: 0-255
}

/**
 * Convert hex color to RGB color space
 *
 * @param hex Hex color string (e.g., '#FF7A59')
 * @returns RGB color representation
 */
function hexToRgb(hex: string): RGB {
  const cleanHex = hex.replace('#', '');

  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
}

/**
 * Convert sRGB hex color to APCA luminance (Y)
 * Uses simple gamma 2.4 power curve as per APCA spec
 *
 * @param hex Hex color string (e.g., '#FF7A59')
 * @returns Luminance value for APCA calculations
 */
function sRgbToY(hex: string): number {
  const rgb = hexToRgb(hex);
  const rLin = Math.pow(rgb.r / 255, APCA_MAIN_TRC);
  const gLin = Math.pow(rgb.g / 255, APCA_MAIN_TRC);
  const bLin = Math.pow(rgb.b / 255, APCA_MAIN_TRC);

  return APCA_SRGB_RED * rLin + APCA_SRGB_GREEN * gLin + APCA_SRGB_BLUE * bLin;
}

/**
 * Apply APCA soft clamp for near-black colors
 * Prevents divide-by-zero and improves accuracy for very dark colors
 *
 * @param Y Luminance value
 * @returns Clamped luminance value
 */
function softClampBlack(Y: number): number {
  if (Y >= APCA_BLACK_THRESHOLD) {
    return Y;
  }
  return Y + Math.pow(APCA_BLACK_THRESHOLD - Y, APCA_BLACK_CLAMP);
}

/**
 * Calculate relative luminance of a color according to WCAG formula
 *
 * Based on WCAG 2.0 guidelines: https://www.w3.org/TR/WCAG20-TECHS/G17.html
 *
 * @param hex Hex color string (e.g., '#FF7A59')
 * @returns Relative luminance value between 0 (darkest) and 1 (lightest)
 */
export function calculateRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);

  // Convert RGB values to sRGB
  const rsRGB = rgb.r / 255;
  const gsRGB = rgb.g / 255;
  const bsRGB = rgb.b / 255;

  // Apply gamma correction
  const r =
    rsRGB <= SRGB_LINEARIZATION_THRESHOLD
      ? rsRGB / SRGB_LINEAR_COEFFICIENT
      : Math.pow(
          (rsRGB + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR,
          SRGB_GAMMA_EXPONENT
        );
  const g =
    gsRGB <= SRGB_LINEARIZATION_THRESHOLD
      ? gsRGB / SRGB_LINEAR_COEFFICIENT
      : Math.pow(
          (gsRGB + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR,
          SRGB_GAMMA_EXPONENT
        );
  const b =
    bsRGB <= SRGB_LINEARIZATION_THRESHOLD
      ? bsRGB / SRGB_LINEAR_COEFFICIENT
      : Math.pow(
          (bsRGB + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR,
          SRGB_GAMMA_EXPONENT
        );

  // Calculate relative luminance using WCAG formula
  return (
    LUMINANCE_RED_COEFFICIENT * r +
    LUMINANCE_GREEN_COEFFICIENT * g +
    LUMINANCE_BLUE_COEFFICIENT * b
  );
}

/**
 * Get the best muted text color for disabled states on a given background color
 *
 * Similar to getBestTextColor but returns muted grays instead of pure black/white
 * to convey a disabled or inactive appearance while maintaining adequate readability.
 *
 * Uses the same hybrid WCAG/APCA approach as getBestTextColor:
 * - Light backgrounds get dark gray for better contrast
 * - Dark backgrounds get light gray for better contrast
 *
 * @param backgroundColor Hex color string of the background
 * @returns '#999999' for light gray text or '#666666' for dark gray text
 */
export function getDisabledTextColor(backgroundColor: string): string {
  const hsl = hexToHsl(backgroundColor);
  const saturation = hsl.s / 100;

  // For highly saturated colors, use APCA
  if (saturation > SATURATION_THRESHOLD) {
    const whiteLc = Math.abs(
      calculateAPCAContrast(PURE_WHITE, backgroundColor)
    );
    const blackLc = Math.abs(
      calculateAPCAContrast(SOFT_BLACK, backgroundColor)
    );
    return blackLc > whiteLc ? '#666666' : '#999999';
  }

  // For non-saturated colors, use WCAG luminance
  const luminance = calculateRelativeLuminance(backgroundColor);
  return luminance > READABLE_LUMINANCE_THRESHOLD ? '#666666' : '#999999';
}

/**
 * Calculate the contrast ratio between two colors according to WCAG formula
 * Based on WCAG 2.0 guidelines: https://www.w3.org/TR/WCAG20-TECHS/G17.html
 * Contrast ratio ranges from 1:1 (no contrast) to 21:1 (black on white)
 *
 * @param foreground Hex color string of the foreground/text color
 * @param background Hex color string of the background color
 * @returns Contrast ratio between 1 and 21
 */
export function calculateContrastRatio(
  foreground: string,
  background: string
): number {
  const l1 = calculateRelativeLuminance(foreground);
  const l2 = calculateRelativeLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Calculate APCA (Accessible Perceptual Contrast Algorithm) contrast
 *
 * APCA is the candidate algorithm for WCAG 3.0 and better handles saturated
 * colors and perceived contrast than WCAG 2.x luminance-based calculations.
 *
 * Returns Lc (Lightness contrast) value:
 * - Positive values: dark text on light background (normal polarity)
 * - Negative values: light text on dark background (reverse polarity)
 * - Range: approximately -108 to +106
 *
 * Recommended minimum Lc values:
 * - Lc 90+: Preferred for body text
 * - Lc 75: Minimum for body text (equivalent to ~4.5:1 WCAG)
 * - Lc 60: Minimum for large text (equivalent to ~3:1 WCAG)
 * - Lc 45: Minimum for non-text elements
 *
 * @param textHex Hex color string of the text color
 * @param bgHex Hex color string of the background color
 * @returns Lc contrast value (use Math.abs for threshold comparisons)
 */
export function calculateAPCAContrast(textHex: string, bgHex: string): number {
  let txtY = sRgbToY(textHex);
  let bgY = sRgbToY(bgHex);

  txtY = softClampBlack(txtY);
  bgY = softClampBlack(bgY);

  let SAPC: number;
  if (bgY > txtY) {
    // Normal polarity (dark text on light background)
    SAPC =
      (Math.pow(bgY, APCA_BG_NORM) - Math.pow(txtY, APCA_TEXT_NORM)) *
      APCA_SCALE;
  } else {
    // Reverse polarity (light text on dark background)
    SAPC =
      (Math.pow(bgY, APCA_REVERSE_BG) - Math.pow(txtY, APCA_REVERSE_TEXT)) *
      APCA_SCALE;
  }

  let Lc: number;
  if (SAPC >= APCA_LOW_CLIP) {
    Lc = SAPC - APCA_LOW_OFFSET;
  } else if (SAPC <= -APCA_LOW_CLIP) {
    Lc = SAPC + APCA_LOW_OFFSET;
  } else {
    Lc = 0;
  }

  return Lc * 100;
}

/**
 * Get the best text color for a given background using a hybrid WCAG/APCA approach.
 *
 * For highly saturated colors (>70% saturation), uses APCA (Accessible Perceptual
 * Contrast Algorithm) which handles saturated colors better perceptually.
 *
 * For non-saturated colors, uses WCAG AA luminance-based selection which
 * guarantees compliance with WCAG 2.x accessibility standards.
 *
 * @param backgroundColor Hex color string of the background
 * @returns Hex color string for text with best contrast
 */
export function getBestTextColor(backgroundColor: string): string {
  const hsl = hexToHsl(backgroundColor);
  const saturation = hsl.s / 100;

  // For highly saturated colors (>70% saturation), use APCA
  // APCA handles saturated colors perceptually better
  if (saturation > SATURATION_THRESHOLD) {
    const whiteLc = Math.abs(
      calculateAPCAContrast(PURE_WHITE, backgroundColor)
    );
    const blackLc = Math.abs(
      calculateAPCAContrast(SOFT_BLACK, backgroundColor)
    );
    return whiteLc > blackLc ? PURE_WHITE : SOFT_BLACK;
  }

  // For non-saturated colors, use WCAG luminance-based selection
  const luminance = calculateRelativeLuminance(backgroundColor);
  const candidate =
    luminance > READABLE_LUMINANCE_THRESHOLD ? SOFT_BLACK : PURE_WHITE;

  // Verify WCAG AA compliance (4.5:1 ratio)
  const contrast = calculateContrastRatio(candidate, backgroundColor);
  if (contrast >= WCAG_AA_MIN_CONTRAST) {
    return candidate;
  }

  // Fall back to pure black/white for better contrast
  return luminance > READABLE_LUMINANCE_THRESHOLD ? PURE_BLACK : PURE_WHITE;
}

/**
 * Get a subtle/muted text color for secondary content like placeholders,
 * hints, and less prominent text
 *
 * Uses the same hybrid WCAG/APCA approach as getBestTextColor.
 *
 * @param backgroundColor Hex color string of the background
 * @returns Hex color string for subtle text
 */
export function getSubtleTextColor(backgroundColor: string): string {
  const hsl = hexToHsl(backgroundColor);
  const saturation = hsl.s / 100;

  // For highly saturated colors, use APCA
  if (saturation > SATURATION_THRESHOLD) {
    const whiteLc = Math.abs(
      calculateAPCAContrast(PURE_WHITE, backgroundColor)
    );
    const blackLc = Math.abs(
      calculateAPCAContrast(SOFT_BLACK, backgroundColor)
    );
    return blackLc > whiteLc ? '#666666' : '#b3b3b3';
  }

  // For non-saturated colors, use WCAG luminance
  const luminance = calculateRelativeLuminance(backgroundColor);
  return luminance > READABLE_LUMINANCE_THRESHOLD ? '#666666' : '#b3b3b3';
}

export function deriveAlertSubtleColor(baseColor: string): string {
  const baseHsl = hexToHsl(baseColor);

  const subtleHsl: HSL = {
    h: baseHsl.h,
    s: Math.max(baseHsl.s * 0.6, 15),
    l: Math.min(Math.max(92, baseHsl.l), 95),
  };

  return hslToHex(subtleHsl);
}
